/*
 *  KAN_UnreadDM.js -- One definition of "you have unread direct messages"
 *  Kanoogi Browser Library - Supports the Commenting System
 *
 *  Created by Claude Opus 5 on Sep 7, 2026
 *  Copyright 2025-2026 Kanoogi Inc. | All Rights Reserved.
 *
 */

"use strict";

/* ---------------------------------------------------------------- */

// Three places show "you have unread DMs" and they have to agree:
//
//   1. the navbar dot        (.nav-unread-dot, driven by optionpanel.js)
//   2. the Direct Messages box in KanChat's left rail (.privchat-box)
//   3. the per-chat dots in the topic list (.topic-item-unread-dot)
//
// They did NOT agree, and that is the bug this module exists to close: the navbar
// dot would light, you would click into KanChat to find out what was new, and the
// DM box carried no indication at all. The per-chat dots are only visible once you
// have already selected the DM forum, so the trail went cold exactly one click in.
//
// 🔴 The subtle half is WHICH CHATS COUNT. optionpanel excludes single-member
// chats — a chat whose only member is you is a notebook, not a conversation, and
// the mascot's own note composer writes one. If the rail computed that rule
// separately and got it slightly wrong, the navbar and the box would disagree,
// which is a more confusing version of the bug being fixed. So the rule lives here
// once and everything imports it.

// CountsAsConversation() - a chat that may legitimately light an unread indicator.
//
// Membership, not the chat's NAME: a name is something anybody can choose, and
// matching on one would let a real conversation somebody happened to title "Notes"
// go silently unannounced.

export function CountsAsConversation(chat)
	{
	if (!chat) return(false);

	return(!(Array.isArray(chat.members) && chat.members.length===1));
	}
/* ---------------------------------------------------------------- */

// HasUnreadDMs() - does this set of chats warrant a dot?
//
// Accepts either the /dms/list array or a map keyed by chatId (KanChat caches them
// as glb.privchatChats), so both callers can hand over what they already hold
// rather than reshaping it at each call site and getting it subtly different.

export function HasUnreadDMs(chats)
	{
	const list=Array.isArray(chats)?chats:(chats&&typeof chats==='object'?Object.values(chats):[]);

	return(list.some(c=>c && c.unread===true && CountsAsConversation(c)));
	}
/* ---------------------------------------------------------------- */

// SetPrivchatBoxDot() - light or clear the Direct Messages box in the left rail.
//
// A no-op when the rail is not on the page, which is every page except KanChat —
// optionpanel polls globally and calls this from everywhere, so "the box does not
// exist here" is the normal case, not an error.

export function SetPrivchatBoxDot(hasUnread)
	{
	const boxes=document.querySelectorAll('.privchat-box');
	for (const b of boxes) b.classList.toggle('has-unread',!!hasUnread);
	}
/* ---------------------------------------------------------------- */

// RememberUnreadDMs() / LastKnownUnreadDMs() - carry the state across the gap
// between the poller and the rail.
//
// optionpanel polls /dms/list on a 60 s timer; KanChat builds the DM box later,
// after /forum/forums resolves. Without this the box would sit undotted until the
// next poll — up to a minute of exactly the symptom being fixed, and most likely
// to happen in the very moment the user clicks the lit navbar dot to go looking.
//
// Parked on window.kanoogi rather than in a module variable because the two sides
// are in different bundles and must not import each other: Kanlibs is shared
// browser code and cannot depend on one service's src/.

export function RememberUnreadDMs(hasUnread)
	{
	if (typeof window==='undefined') return;
	window.kanoogi=window.kanoogi||{};
	window.kanoogi.unreadDMs=!!hasUnread;
	}

export function LastKnownUnreadDMs()
	{
	return(typeof window!=='undefined' && !!(window.kanoogi && window.kanoogi.unreadDMs));
	}
